/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

/**
 * ResizableStage
 * --------------
 * Wraps any child and lets the user uniformly scale it by dragging a
 * bottom-right corner handle that only appears on hover (or while dragging).
 * The chosen scale is persisted to localStorage under `storageKey` so the
 * companion/bubble keeps its size across sessions.
 *
 * The scale is applied as a CSS `transform: scale()` on the stage, so it never
 * disturbs the surrounding flex/absolute layout — the element grows/shrinks in
 * place around its transform-origin.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import styles from './index.module.less';

interface ResizableStageProps {
  /** localStorage key used to persist the scale. */
  storageKey: string;
  children: ReactNode;
  /** Minimum uniform scale. */
  min?: number;
  /** Maximum uniform scale. */
  max?: number;
  /** Initial scale used before the user drags / when nothing is stored. */
  defaultScale?: number;
  className?: string;
  style?: CSSProperties;
  /** transform-origin for the scale (defaults to center center). */
  origin?: string;
  /** Accessible label for the resize handle. */
  handleLabel?: string;
  /** Enable drag-to-resize. Disable for elements that should only be movable. */
  resizable?: boolean;
  /** Enable drag-to-move (repositioning). The offset is persisted separately. */
  movable?: boolean;
  /** localStorage key used to persist the move offset (defaults to `${storageKey}_pos`). */
  offsetStorageKey?: string;
  /** When set, positions the resize handle at the bottom-right of this content
   *  rect (fractions 0..1 of the stage) instead of the stage's own corner, so
   *  the handle hugs the visible character rather than empty letterbox space. */
  handleRect?: { left: number; top: number; width: number; height: number } | null;
  /** Optional control revealed on hover at the character's top-left corner
   *  (positioned via `handleRect` when provided). Used e.g. for the background
   *  audio toggle so it hugs the avatar and only shows on hover. */
  cornerAction?: ReactNode;
}

const clampScale = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// Keep at least this many pixels of the movable element inside the viewport in
// each axis, so it can never be dragged fully off-screen (and, crucially, can't
// persist an off-screen offset that makes it "disappear" on the next load).
const MIN_VISIBLE = 64;

interface StageBase {
  left: number;
  top: number;
  width: number;
  height: number;
}

// The move offset is applied as a CSS translate on top of the element's layout
// box. `deriveBase` recovers that untranslated box from the live rect and the
// offset currently applied to the DOM, so clamping math works regardless of the
// current translate/scale.
const deriveBase = (stage: HTMLDivElement | null, appliedOffset: Offset): StageBase | null => {
  if (!stage) return null;
  const rect = stage.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return {
    left: rect.left - appliedOffset.x,
    top: rect.top - appliedOffset.y,
    width: rect.width,
    height: rect.height,
  };
};

// Clamp a candidate offset so the element's box stays at least MIN_VISIBLE px
// inside the viewport on every edge.
const clampOffsetWithBase = (base: StageBase, candidate: Offset): Offset => {
  if (typeof window === 'undefined') return candidate;
  const visX = Math.min(MIN_VISIBLE, base.width);
  const visY = Math.min(MIN_VISIBLE, base.height);
  const minLeft = visX - base.width;
  const maxLeft = window.innerWidth - visX;
  const minTop = visY - base.height;
  const maxTop = window.innerHeight - visY;
  const left = clampScale(base.left + candidate.x, minLeft, maxLeft);
  const top = clampScale(base.top + candidate.y, minTop, maxTop);
  return { x: left - base.left, y: top - base.top };
};

const readStoredScale = (storageKey: string, fallback: number, min: number, max: number) => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return fallback;
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed)) return fallback;
    return clampScale(parsed, min, max);
  } catch {
    return fallback;
  }
};

interface Offset {
  x: number;
  y: number;
}

const readStoredOffset = (storageKey: string): Offset => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return { x: 0, y: 0 };
    const parsed = JSON.parse(raw);
    const x = Number(parsed?.x);
    const y = Number(parsed?.y);
    return {
      x: Number.isFinite(x) ? x : 0,
      y: Number.isFinite(y) ? y : 0,
    };
  } catch {
    return { x: 0, y: 0 };
  }
};

interface DragState {
  pointerStartX: number;
  pointerStartY: number;
  startScale: number;
  baseWidth: number;
}

interface MoveState {
  pointerStartX: number;
  pointerStartY: number;
  startOffset: Offset;
}

function ResizableStage({
  storageKey,
  children,
  min = 0.6,
  max = 1.8,
  defaultScale = 1,
  className = '',
  style,
  origin = 'center center',
  handleLabel = '拖拽调整大小',
  resizable = true,
  movable = false,
  offsetStorageKey,
  handleRect,
  cornerAction,
}: ResizableStageProps) {
  const posKey = offsetStorageKey || `${storageKey}_pos`;
  const [scale, setScale] = useState(() => readStoredScale(storageKey, defaultScale, min, max));
  const [offset, setOffset] = useState<Offset>(() =>
    movable ? readStoredOffset(posKey) : { x: 0, y: 0 }
  );
  const [dragging, setDragging] = useState(false);
  const [moving, setMoving] = useState(false);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const moveStateRef = useRef<MoveState | null>(null);

  // Re-hydrate when the persistence key changes (e.g. switching companions).
  useEffect(() => {
    if (!resizable) return;
    setScale(readStoredScale(storageKey, defaultScale, min, max));
  }, [storageKey, defaultScale, min, max, resizable]);

  useEffect(() => {
    if (movable) setOffset(readStoredOffset(posKey));
  }, [posKey, movable]);

  const persistScale = useCallback(
    (next: number) => {
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        /* ignore quota / privacy-mode errors */
      }
    },
    [storageKey]
  );

  const persistOffset = useCallback(
    (next: Offset) => {
      try {
        window.localStorage.setItem(posKey, JSON.stringify(next));
      } catch {
        /* ignore quota / privacy-mode errors */
      }
    },
    [posKey]
  );

  useEffect(() => {
    if (!resizable || !dragging) return;
    const handleMouseMove = (event: MouseEvent) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      // Diagonal drag: take the dominant axis so the gesture feels natural.
      const delta = Math.max(
        event.clientX - drag.pointerStartX,
        event.clientY - drag.pointerStartY
      );
      const base = drag.baseWidth || 200;
      const next = clampScale(drag.startScale + delta / base, min, max);
      setScale(next);
    };
    const handleMouseUp = () => {
      dragStateRef.current = null;
      setDragging(false);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, min, max, resizable]);

  useEffect(() => {
    if (!moving) return;
    const handleMouseMove = (event: MouseEvent) => {
      const move = moveStateRef.current;
      if (!move) return;
      const candidate = {
        x: move.startOffset.x + (event.clientX - move.pointerStartX),
        y: move.startOffset.y + (event.clientY - move.pointerStartY),
      };
      // Recover the untranslated box using the offset that was live when the
      // gesture began, then clamp so the element stays partly on-screen.
      const base = deriveBase(stageRef.current, move.startOffset);
      setOffset(base ? clampOffsetWithBase(base, candidate) : candidate);
    };
    const handleMouseUp = () => {
      moveStateRef.current = null;
      setMoving(false);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [moving]);

  // Persist whenever the scale settles (fires on every change; cheap enough).
  useEffect(() => {
    if (!resizable) return;
    persistScale(scale);
  }, [scale, persistScale, resizable]);

  useEffect(() => {
    if (movable) persistOffset(offset);
  }, [offset, movable, persistOffset]);

  // Pull a persisted (or now-oversized) offset back inside the viewport. Runs
  // once the DOM has the transform applied, and again whenever the window
  // resizes, so an offset stored from a larger window — or an off-screen value
  // left over before clamping existed — can never keep the element hidden.
  useEffect(() => {
    if (!movable) return undefined;
    const reclamp = () => {
      setOffset((current) => {
        const base = deriveBase(stageRef.current, current);
        if (!base) return current;
        const next = clampOffsetWithBase(base, current);
        return next.x === current.x && next.y === current.y ? current : next;
      });
    };
    reclamp();
    window.addEventListener('resize', reclamp);
    return () => window.removeEventListener('resize', reclamp);
  }, [movable, posKey]);

  const handleResizeStart = (event: ReactMouseEvent<HTMLButtonElement>) => {
    if (!resizable) return;
    event.preventDefault();
    event.stopPropagation();
    const stage = stageRef.current;
    // offsetWidth is the pre-transform layout width, so it's already our
    // scale=1 base regardless of the current CSS transform.
    const baseWidth = stage ? stage.offsetWidth : 200;
    dragStateRef.current = {
      pointerStartX: event.clientX,
      pointerStartY: event.clientY,
      startScale: scale,
      baseWidth,
    };
    setDragging(true);
  };

  const effectiveScale = resizable ? scale : 1;

  const handleMoveStart = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!movable) return;
    event.preventDefault();
    moveStateRef.current = {
      pointerStartX: event.clientX,
      pointerStartY: event.clientY,
      startOffset: offset,
    };
    setMoving(true);
  };

  return (
    <div
      ref={stageRef}
      className={`${styles.stage} ${dragging ? styles.dragging : ''} ${
        moving ? styles.moving : ''
      } ${className}`}
      style={{
        ...style,
        transform: `translate(${offset.x}px, ${offset.y}px) scale(${effectiveScale})`,
        transformOrigin: origin,
        // Exposed so descendant controls (resize handle / corner action) can
        // counter-scale by 1/scale and keep a fixed visual size as the stage grows.
        ['--stage-scale' as string]: String(effectiveScale),
      }}
    >
      {movable ? (
        <div
          className={styles.moveSurface}
          onMouseDown={handleMoveStart}
          aria-label="拖拽移动位置"
          title="拖拽移动位置"
        />
      ) : null}
      <div className={styles.content}>{children}</div>
      {cornerAction ? (
        <div
          className={styles.cornerAction}
          // Anchor to the character's top-left when a content rect is known, so
          // it hugs the art instead of the empty letterbox corner.
          style={
            handleRect
              ? ({
                  left: `${handleRect.left * 100}%`,
                  top: `${handleRect.top * 100}%`,
                } as CSSProperties)
              : undefined
          }
        >
          {cornerAction}
        </div>
      ) : null}
      {resizable ? (
        <button
          type="button"
          className={styles.handle}
          onMouseDown={handleResizeStart}
          aria-label={handleLabel}
          title={handleLabel}
          style={
            handleRect
              ? ({
                  // Anchor the handle's bottom-right corner to the character's
                  // bottom-right so it hugs the art instead of the empty box.
                  // The -100% shift rides in CSS vars so the hover scale pop-in
                  // (which also uses `transform`) is preserved.
                  right: 'auto',
                  bottom: 'auto',
                  left: `${(handleRect.left + handleRect.width) * 100}%`,
                  top: `${(handleRect.top + handleRect.height) * 100}%`,
                  '--handle-shift-x': '-100%',
                  '--handle-shift-y': '-100%',
                } as CSSProperties)
              : undefined
          }
        />
      ) : null}
    </div>
  );
}

export default ResizableStage;
